import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {BreadcrumbService} from '@cdss/services/breadcrumb.service';
import {OrganisationsService} from '@shared/api/services/v2/organisations.service';
import {isAlreadyNotified} from '@shared/api/services/v2/api-request';
import {ORGANISATIONS_LIST_ROUTE, organisationDetailRoute} from '@shared/services/router/organisation-routes';
import {
    ORGANISATION_CODE_PREFIX,
    OrganisationFormComponent,
    OrganisationFormValue
} from '../form/organisation-form.component';

/** The API answers a duplicate code with this identifier (422). */
const DUPLICATE_CODE = 'organisation_code_already_exists';

/**
 * UNIVAPLAT-3692: New organisation page (route `cdss/admin/organisations/new`, CREATE_ORGANISATION).
 * The backend owns the error text; nothing from the form or the response is logged.
 */
@Component({
    selector: 'cdss-organisation-new-page',
    standalone: true,
    imports: [OrganisationFormComponent],
    templateUrl: './organisation-new.page.html',
    styleUrls: ['./organisation-new.page.scss']
})
export class OrganisationNewPage implements OnInit {
    readonly listRoute = '/' + ORGANISATIONS_LIST_ROUTE;
    readonly codePrefix = ORGANISATION_CODE_PREFIX;

    saving = false;
    codeError: string | null = null;

    constructor(
        private breadcrumbService: BreadcrumbService,
        private organisationsService: OrganisationsService,
        private toastr: ToastrService,
        private router: Router,
    ) {
    }

    ngOnInit(): void {
        this.breadcrumbService.set([
            {title: 'Organisations', url: this.listRoute},
            {title: 'New organisation', url: null as unknown as string},
        ]);
    }

    async onSave(value: OrganisationFormValue): Promise<void> {
        if (this.saving) {
            return;
        }
        this.saving = true;
        this.codeError = null;
        try {
            const created = await this.organisationsService.create(value);
            this.toastr.success('Organisation created', 'Success');
            await this.router.navigateByUrl('/' + organisationDetailRoute(created.id));
        } catch (error: any) {
            const message: string | undefined = error?.error?.error;
            if (this.isDuplicateCode(error)) {
                this.codeError = message || 'This organisation code already exists.';
            } else if (!isAlreadyNotified(error)) {
                this.toastr.error(message || 'Something went wrong. Please try again.');
            }
        } finally {
            this.saving = false;
        }
    }

    private isDuplicateCode(error: any): boolean {
        const body = error?.error;
        return error?.status === 422 && [body?.code, body?.error, body?.message, body?.detail]
            .some(field => typeof field === 'string' && field.includes(DUPLICATE_CODE));
    }
}
